import { useEffect, useRef } from 'react';
import type { Player } from '@vimeo/player';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';

type UseVimeoPlaybackSignalArgs = Readonly<{
  /** Attach only when the iframe hosts a player.vimeo.com embed. */
  enabled: boolean;
  /** Identity of the embedded video (URL or id); re-binds the player when it changes. */
  videoKey: string | undefined;
  onPlaying: () => void;
  onError: () => void;
}>;

/**
 * Reveals a Vimeo background video only when frames are actually rendering.
 * The iframe `load` event is the wrong signal twice over: it fires when the embed
 * document loads (well before playback, hiding the poster over a black player),
 * and for an SSR'd iframe it can fire before hydration so React never sees it and
 * the video stays hidden forever. The player postMessage handshake has neither problem.
 */
export const useVimeoPlaybackSignal = ({ enabled, videoKey, onPlaying, onError }: UseVimeoPlaybackSignalArgs) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const handlePlaying = useEventCallback(onPlaying);
  const handleError = useEventCallback(onError);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!enabled || !videoKey || !iframe) return;

    let player: Player | null = null;
    let cancelled = false;

    import(
      /* webpackChunkName: "vimeo-player" */
      '@vimeo/player'
    )
      .then(({ default: VimeoPlayer }) => {
        if (cancelled) return;
        player = new VimeoPlayer(iframe);
        player.on('playing', handlePlaying);
        player.on('error', handleError);
        // The embed can already be mid-playback by the time this chunk loads,
        // and 'playing' won't re-fire for a video that's already running.
        player.getPaused().then(
          paused => {
            if (!cancelled && !paused) handlePlaying();
          },
          error => {
            addError(error, { context: 'useVimeoPlaybackSignal#getPaused' });
            // State unknown: reveal rather than risk staying hidden forever.
            if (!cancelled) handlePlaying();
          },
        );
      })
      .catch(error => {
        addError(error, { context: 'useVimeoPlaybackSignal#loadPlayerLib' });
        // Without the API we can't see playback start; reveal so the video is never stuck hidden.
        if (!cancelled) handlePlaying();
      });

    return () => {
      cancelled = true;
      // No destroy(): the lib tears down the React-owned iframe. off() is enough.
      player?.off('playing', handlePlaying);
      player?.off('error', handleError);
    };
  }, [enabled, videoKey, handlePlaying, handleError]);

  return iframeRef;
};
